<template>
    <div class="ix-dashboard-block bordered">
        <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 10px;">
            <h2 class="ix-page-title-secondary mb-0">
                Réceptions fournisseurs valorisées
                <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="Valorisation au prix d'achat remisé de la commande fournisseur, sur la quantité réellement reçue."></i>
            </h2>
            <div class="d-flex align-items-end flex-wrap" style="gap: 8px;">
                <div class="form-group mb-0">
                    <label class="mb-1 d-block text-muted" style="font-size: 12px;">Période</label>
                    <div class="d-flex" style="gap: 6px;">
                        <button v-for="preset in presets"
                                :key="preset.label"
                                type="button"
                                class="ix-btn ix-btn-small"
                                :class="{ blue: isActivePreset(preset) }"
                                @click="applyPreset(preset)">
                            {{ preset.label }}
                        </button>
                    </div>
                </div>
                <div class="form-group mb-0">
                    <label for="receptions-valuation-start" class="mb-1 d-block text-muted" style="font-size: 12px;">Du</label>
                    <input type="date" id="receptions-valuation-start" class="form-control form-control-sm" style="width: auto;" v-model="startAt" @change="fetchValuation">
                </div>
                <div class="form-group mb-0">
                    <label for="receptions-valuation-end" class="mb-1 d-block text-muted" style="font-size: 12px;">Au</label>
                    <input type="date" id="receptions-valuation-end" class="form-control form-control-sm" style="width: auto;" v-model="endAt" @change="fetchValuation">
                </div>
            </div>
        </div>

        <div class="row mt-3">
            <div class="col-sm-12 col-md-3">
                <div class="ix-count-box">
                    <h2 class="ix-count-box-title">Total réceptionné HT</h2>
                    <span class="ix-count-box-value">{{ loading ? '-' : formatCurrency(totals.total_ht) }}</span>
                </div>
            </div>
            <div class="col-sm-12 col-md-3">
                <div class="ix-count-box">
                    <h2 class="ix-count-box-title">Total réceptionné TTC</h2>
                    <span class="ix-count-box-value">{{ loading ? '-' : formatCurrency(totals.total_ttc) }}</span>
                </div>
            </div>
            <div class="col-sm-12 col-md-3">
                <div class="ix-count-box">
                    <h2 class="ix-count-box-title">
                        Dont entré en stock HT
                        <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="Lignes article non affectées à une affaire : ce sont les seules qui génèrent un bon d'entrée en stock à la clôture de la réception."></i>
                    </h2>
                    <span class="ix-count-box-value">{{ loading ? '-' : formatCurrency(totals.stock_total_ht) }}</span>
                </div>
            </div>
            <div class="col-sm-12 col-md-3">
                <div class="ix-count-box">
                    <h2 class="ix-count-box-title">
                        Dont affecté aux affaires HT
                        <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="Lignes réceptionnées directement sur une affaire ou une intervention GMAO : consommées sans passer par le stock."></i>
                    </h2>
                    <span class="ix-count-box-value">{{ loading ? '-' : formatCurrency(totals.deals_total_ht) }}</span>
                </div>
            </div>
        </div>

        <div class="ix-table-box mt-3">
            <table class="ix-table ix-table-sm">
                <thead>
                    <tr>
                        <th>Article</th>
                        <th class="text-center">Réceptions</th>
                        <th class="text-center">Quantité réceptionnée</th>
                        <th class="text-center">Unité</th>
                        <th class="text-right">Total HT</th>
                        <th class="text-right">Total TTC</th>
                    </tr>
                </thead>
                <tbody v-if="loading">
                    <tr>
                        <td colspan="6" class="text-center"><i class="far fa-circle-notch fa-spin"></i></td>
                    </tr>
                </tbody>
                <tbody v-else-if="rows.length === 0">
                    <tr>
                        <td colspan="6" class="text-center text-muted">Aucune réception clôturée sur la période</td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr v-for="row in rows" :key="row.article_id || 'no-article'">
                        <td>
                            <a v-if="row.url" :href="row.url" class="ix-link">{{ row.name }}</a>
                            <span v-else class="text-muted">{{ row.name }}</span>
                            <span v-if="row.reference" class="d-block text-muted" style="font-size: 12px;">{{ row.reference }}</span>
                        </td>
                        <td class="text-center">
                            <a v-if="row.receptions_url" :href="row.receptions_url" class="ix-link">{{ row.receptions_count }}</a>
                            <span v-else class="text-muted">{{ row.receptions_count }}</span>
                        </td>
                        <td class="text-center">{{ formatQuantity(row.quantity) }}</td>
                        <td class="text-center">{{ row.unit || '-' }}</td>
                        <td class="text-right">{{ formatCurrency(row.total_ht) }}</td>
                        <td class="text-right">{{ formatCurrency(row.total_ttc) }}</td>
                    </tr>
                </tbody>
                <tfoot v-if="!loading && rows.length > 0">
                    <tr>
                        <th>Total</th>
                        <th></th>
                        <th class="text-center text-muted" style="font-weight: normal;">
                            <span data-toggle="tooltip" data-container="body" title="Non cumulable : les articles n'ont pas la même unité.">-</span>
                        </th>
                        <th></th>
                        <th class="text-right">{{ formatCurrency(totals.total_ht) }}</th>
                        <th class="text-right">{{ formatCurrency(totals.total_ttc) }}</th>
                    </tr>
                </tfoot>
            </table>
        </div>

        <p class="text-muted mt-2 mb-0" style="font-size: 12px;">
            Réceptions clôturées du {{ period.label }}
            <a v-if="receptionsUrl" :href="receptionsUrl" class="ix-link ml-1">
                Voir toutes les réceptions de la période
            </a>
        </p>
    </div>
</template>

<script>
import $ from "jquery";

export default {
    props: {
        routeReceptionsValuation: String,
    },
    data() {
        const now = new Date();

        return {
            startAt: this.toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
            endAt: this.toInputDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
            loading: false,
            period: { label: '' },
            receptionsUrl: null,
            totals: {
                total_ht: 0,
                total_ttc: 0,
                stock_total_ht: 0,
                deals_total_ht: 0,
            },
            rows: [],
        }
    },
    computed: {
        presets() {
            const now = new Date();

            return [
                {
                    label: 'Mois en cours',
                    start: this.toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
                    end: this.toInputDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
                },
                {
                    label: 'Mois dernier',
                    start: this.toInputDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
                    end: this.toInputDate(new Date(now.getFullYear(), now.getMonth(), 0)),
                },
                {
                    label: 'Année en cours',
                    start: this.toInputDate(new Date(now.getFullYear(), 0, 1)),
                    end: this.toInputDate(new Date(now.getFullYear(), 11, 31)),
                },
            ];
        },
    },
    methods: {
        toInputDate(date) {
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            return date.getFullYear() + '-' + month + '-' + day;
        },
        formatQuantity(value) {
            return Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 3 });
        },
        formatCurrency(value) {
            return Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
        },
        isActivePreset(preset) {
            return this.startAt === preset.start && this.endAt === preset.end;
        },
        applyPreset(preset) {
            this.startAt = preset.start;
            this.endAt = preset.end;
            this.fetchValuation();
        },
        async fetchValuation() {
            if (!this.startAt || !this.endAt || this.startAt > this.endAt) {
                return;
            }

            this.loading = true;

            const query = '?start_at=' + this.startAt + '&end_at=' + this.endAt;

            await fetch(this.routeReceptionsValuation + query, {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                return response.json();
            }).then((data) => {
                this.rows = data.rows;
                this.totals = data.totals;
                this.period = data.period;
                this.receptionsUrl = data.receptions_url;
            }).catch(() => {
                this.rows = [];
                this.receptionsUrl = null;
            });

            this.loading = false;
            this.$nextTick(() => {
                $('[data-toggle="tooltip"]:not(.copy-button)').tooltip();
            });
        },
    },
    mounted() {
        this.fetchValuation();
    }
}
</script>
